// [DS-MIGRATION] legacy Breadcrumbs - replaced by DS-backed Breadcrumb. Remove in follow-up PR.
// import Breadcrumbs from "@/componentsDS/Breadcrumbs/index.jsx";
import { Grid, Box } from "@dextech-ai/dex-design-system";
import Breadcrumb from '@/dsComponents/Breadcrumb';
import { assetUrl } from '@/utils/asset-url';
import useDataSchemaVersioning from "@/hooks/use-data-schema-versioning/index";
import usePage from "@/hooks/use-page";
import { useNavigate, useParams } from "@/lib/router-dom";
import PropTypes from 'prop-types';
import { useEffect, useMemo, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { useDsvAdmin } from "@/hooks/use-dsv-admin/index";
import LinkUseCasePersonaModal from "./dsv-link-usecase-persona-modal";
import useToast from "@/hooks/use-toast";
import Toast from "@/components/Toast";
import DeleteVersionModal from "./components/DeleteVersionModal";
import EditVersionNumberModal from "./components/EditVersionNumberModal";
import UnlinkUseCaseModal from "./components/UnlinkUseCaseModal";
import DeLinksSection, { groupLinks } from "./components/DeLinksSection";
import AffectedOrganisations from "./components/AffectedOrganisations";
import VersionActorLine from "./components/VersionActorLine";

// ============ Helper Functions ============

function formatDate(dateStr) {
	if (!dateStr) return '-';
	const date = new Date(dateStr);
	return date.toISOString().split('T')[0];
}

function getFieldCount(schema) {
	if (!schema) return 0;
	const parsed = typeof schema === 'string' ? JSON.parse(schema) : schema;
	return parsed?.properties ? Object.keys(parsed.properties).length : 0;
}

// ============ Status Badge ============

function StatusBadge({ status }) {
	const statusMap = {
		'ACTIVE': { className: 'dsv-status--active', label: 'Active' },
		'DRAFT': { className: 'dsv-status--draft', label: 'Draft' },
		'DEPRECATED': { className: 'dsv-status--migrating', label: 'Deprecated' },
		'RETIRED': { className: 'dsv-status--retired', label: 'Retired' },
	};
	const config = statusMap[status] || statusMap['DRAFT'];
	return <span className={`dsv-status ${config.className}`}>{config.label}</span>;
}

StatusBadge.propTypes = { status: PropTypes.string };

// ============ Change Type Badge ============

function ChangeTypeBadge({ changeType }) {
	const typeMap = {
		'PATCH': { className: 'dsv-change-type--patch', label: 'Patch' },
		'MINOR': { className: 'dsv-change-type--minor', label: 'Minor' },
		'MAJOR': { className: 'dsv-change-type--major', label: 'Major' },
		'INITIAL': { className: 'dsv-change-type--initial', label: 'Initial' },
	};
	const config = typeMap[changeType] || typeMap['INITIAL'];
	return <span className={`dsv-change-type ${config.className}`}>{config.label}</span>;
}

ChangeTypeBadge.propTypes = { changeType: PropTypes.string };

// ============ Summary Cards ============

function SummaryCards({ currentVersion, status, totalFields, affectedOrganisations }) {
	return (
		<Grid columns={{ xs: 1, sm: 3 }} columnGap={2} rowGap={2} mb={4}>
			<div className="dsv-detail__summary-card">
				<div className="dsv-detail__summary-card__label">Current Version</div>
				<div className="dsv-detail__summary-card__value">
					{currentVersion}
					<StatusBadge status={status} />
				</div>
			</div>
			<div className="dsv-detail__summary-card">
				<div className="dsv-detail__summary-card__label">Data Fields</div>
				<div className="dsv-detail__summary-card__value">{totalFields}</div>
			</div>
			<div className="dsv-detail__summary-card">
				<div className="dsv-detail__summary-card__label">Affected Organisations</div>
				<div className="dsv-detail__summary-card__value">{affectedOrganisations}</div>
			</div>
		</Grid>
	);
}

SummaryCards.propTypes = {
	currentVersion: PropTypes.string,
	status: PropTypes.string,
	totalFields: PropTypes.number,
	affectedOrganisations: PropTypes.number,
};

// ============ Version History Item ============

function VersionHistoryItem({ version, onViewSchema, canManage, onDelete, onEdit }) {
	const isDraft = version.status === 'DRAFT';
	return (
		<div className="dsv-version-item">
			<div className="dsv-version-item__left">
				<Box display="flex" align="center" gap={2} mb={2}>
					{canManage && isDraft ? (
						<button
							type="button"
							className="dsv-version-item__icon dsv-version-item__icon--edit"
							title="Edit version number"
							data-testid={`edit-version-btn-${version.version}`}
							onClick={() => onEdit(version)}
						>
							&#9998;
						</button>
					) : (
						<span className="dsv-version-item__icon">&#9998;</span>
					)}
					<span className="dsv-version-item__title">Version {version.version}</span>
					<ChangeTypeBadge changeType={version.changeType} />
					<StatusBadge status={version.status} />
				</Box>
				<div className="dsv-version-item__description">
					{version.changeDescription || 'No description'}
				</div>
				<div className="dsv-version-item__meta">
					{version.publishedDate && (
						<span className="dsv-version-item__meta-item">
							Published: {formatDate(version.publishedDate)}
						</span>
					)}
					{version.retiredDate && (
						<span className="dsv-version-item__meta-item">
							Retired: {formatDate(version.retiredDate)}
						</span>
					)}
					{version.expiry && version.status !== 'ACTIVE' && (
						<span className="dsv-version-item__meta-item">
							Expires: {formatDate(version.expiry)}
						</span>
					)}
				</div>
					<VersionActorLine
						createdByEmail={version.createdByEmail}
						modifiedByEmail={version.modifiedByEmail}
						updatedAt={version.updatedAt}
					/>
			</div>
			<Box display="flex" align="center" gap={2}>
				<button
					className="dsv-btn dsv-btn--outline"
					onClick={() => onViewSchema(version)}
				>
					View Schema
				</button>
				{canManage && isDraft && (
					<button
						className="dsv-btn dsv-btn--retire dsv-btn--sm"
						style={{ marginLeft: 8 }}
						data-testid={`delete-version-btn-${version.version}`}
						onClick={() => onDelete(version)}
					>
						Delete
					</button>
				)}
			</Box>
		</div>
	);
}

VersionHistoryItem.propTypes = {
	version: PropTypes.object,
	onViewSchema: PropTypes.func,
	canManage: PropTypes.bool,
	onDelete: PropTypes.func,
	onEdit: PropTypes.func,
};

// ============ Main Page Component ============

const DSVDetailPage = () => {
	const { goTo } = useNavigate();
	const { dataElementId } = useParams();
	const canEditSchema = useDsvAdmin();

	const {
		loading,
		currentVersionDetail,
		versionHistory,
		fetchAllVersions,
		impactAnalysis,
		fetchImpactAnalysis,
		fetchDeLinks,
		removeDeLink,
		deleteVersion,
		updateVersionNumber,
	} = useDataSchemaVersioning();

	const [linkModalVisible, setLinkModalVisible] = useState(false);
	const [deLinks, setDeLinks] = useState([]);
	// Shared accordion: which toggle panel is open below the toggles row.
	const [expandedPanel, setExpandedPanel] = useState(null); // null | 'orgs' | 'links'

	const { toastList, addToastMessage } = useToast();
	const [deleteTarget, setDeleteTarget] = useState(null);
	const [editTarget, setEditTarget] = useState(null);
	const [editServerError, setEditServerError] = useState('');
	const [unlinkTarget, setUnlinkTarget] = useState(null);

	const pageTitle = currentVersionDetail?.elementName || "Data Element Overview";
	const { breadcrumbsItems } = usePage({
		title: pageTitle,
		breadcrumbs: [
			{ label: '', link: '/' },
			{ label: 'Data Element', link: '/dsv' },
			{ label: pageTitle, link: '' },
		],
	});

	useEffect(() => {
		if (dataElementId) {
			fetchAllVersions(dataElementId);
		}
	}, [dataElementId]);

	// Fetch impact analysis once we have a current version
	useEffect(() => {
		if (currentVersionDetail?.version && dataElementId) {
			fetchImpactAnalysis(dataElementId, currentVersionDetail.version);
		}
	}, [currentVersionDetail?.version]);

	// Track mount status so async fetches never setState after unmount.
	const isMountedRef = useRef(true);
	useEffect(() => () => { isMountedRef.current = false; }, []);

	// Single guarded fetch helper shared by the mount effect and refreshDeLinks.
	function loadDeLinks() {
		if (!dataElementId) return;
		fetchDeLinks(dataElementId).then((links) => {
			if (!isMountedRef.current) return;
			setDeLinks(Array.isArray(links) ? links : []);
		});
	}

	// Load the existing use-case-persona links so they can be listed and unlinked.
	useEffect(() => {
		loadDeLinks();
	}, [dataElementId]);

	function refreshDeLinks() {
		loadDeLinks();
	}

	function handleConfirmUnlink(target) {
		setUnlinkTarget(null);
		const ids = Array.isArray(target?.dataElementPersonaIds) ? target.dataElementPersonaIds : [];
		if (ids.length === 0) return;
		Promise.all(ids.map((id) => removeDeLink(id))).then((results) => {
			// removeDeLink resolves `{ error }` on failure, not a thrown error.
			const failed = results.find((res) => res?.error);
			if (failed) {
				const msg = typeof failed.error === 'string'
					? failed.error
					: (failed.error?.message || 'Failed to remove link');
				addToastMessage(msg, 'error');
			} else {
				addToastMessage(`Removed ${target.personaName} from ${target.useCaseName}`, 'success');
			}
			// Refetch either way so the list reflects what was actually removed
			// (a partial failure may still have deleted some rows).
			refreshDeLinks();
		}).catch(() => {
			addToastMessage('Failed to remove link', 'error');
			refreshDeLinks();
		});
	}

	// ============ Navigation Handlers ============

	function handleBackToList() {
		goTo('/dsv');
	}

	function handleUpdateDataSchema() {
		const latestVersion = versionHistory.length > 0 ? versionHistory[0].version : null;
		const query = latestVersion ? `?version=${latestVersion}&mode=create` : '?mode=create';
		goTo(`/dsv/${dataElementId}/update-schema${query}`);
	}

	function handleCompareVersions() {
		goTo(`/dsv/${dataElementId}/compare`);
	}

	function handleViewSchema(version) {
		goTo(`/dsv/${dataElementId}/update-schema?version=${version.version}`);
	}

	function handleConfirmDelete(version) {
		setDeleteTarget(null);
		deleteVersion(dataElementId, version.version).then((res) => {
			if (res?.error) {
				const msg = typeof res.error === 'string' ? res.error : (res.error?.message || `Failed to delete v${version.version}`);
				addToastMessage(msg, 'error');
				return;
			}
			addToastMessage(`Version ${version.version} deleted`, 'success');
			fetchAllVersions(dataElementId);
		}).catch(() => {
			addToastMessage(`Failed to delete v${version.version}`, 'error');
		});
	}

	function handleConfirmEdit(version, newVersion) {
		setEditServerError('');
		updateVersionNumber(dataElementId, version.version, newVersion).then((res) => {
			if (res?.error) {
				const msg = res.conflict
					? `Version ${newVersion} already exists for this data element.`
					: (res.error?.message || `Failed to rename v${version.version}`);
				setEditServerError(msg);
				return;
			}
			setEditTarget(null);
			addToastMessage(`Version renamed to ${newVersion}`, 'success');
			fetchAllVersions(dataElementId);
		}).catch(() => {
			setEditServerError(`Failed to rename v${version.version}`);
		});
	}

	const fieldCount = currentVersionDetail
		? getFieldCount(currentVersionDetail.elementSchemaJson || currentVersionDetail.elementSchema)
		: 0;

	// Toggle counts: affected orgs (raw org rows) and linked use cases (grouped,
	// so the count matches distinct use cases rather than flat persona rows).
	const orgs = impactAnalysis?.organizations || [];
	const linkedUseCaseCount = groupLinks(deLinks).length;

	// Surface the schema's root `description` keyword (annotation) on the
	// detail page header. Distinct from `changeDescription`, which is the
	// per-version bump note authored in the Confirm modal.
	const schemaDescription = useMemo(() => {
		const schema = currentVersionDetail?.elementSchemaJson || currentVersionDetail?.elementSchema;
		const parsed = typeof schema === 'string' ? (() => {
			try { return JSON.parse(schema); } catch { return null; }
		})() : schema;
		const desc = parsed?.description;
		return typeof desc === 'string' && desc.trim() ? desc : '';
	}, [currentVersionDetail]);

	return (
		<>
			{/* [DS-MIGRATION] legacy Breadcrumbs - replaced by DS-backed Breadcrumb. Remove in follow-up PR. */}
			{/* <Breadcrumbs breadcrumbItems={breadcrumbsItems} /> */}
			<Breadcrumb items={breadcrumbsItems} />
			{/* Back link */}
			<div
				className="dsv-detail__back"
				role="button"
				tabIndex={0}
				onClick={handleBackToList}
				onKeyDown={(e) => {
					if (e.key === 'Enter' || e.key === ' ') {
						e.preventDefault();
						handleBackToList();
					}
				}}
			>
				<span className="dsv-detail__back-arrow">&larr;</span>
				<span>Back to Data Element</span>
			</div>

			{/* Header */}
			<Box display="flex" justify="between" align="start" pt={2} pb={4}>
				<Box display="flex" align="start">
					<Box mr={3} mt={1}>
						<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
							<rect width="24" height="24" rx="4" fill="#EEF0FF" />
							<path d="M7 8h10M7 12h10M7 16h6" stroke="#4F5AED" strokeWidth="1.5" strokeLinecap="round" />
						</svg>
					</Box>
					<div className="dsv-detail__header-info">
						<h1 className="dsv-detail__title">{currentVersionDetail?.elementName}</h1>
						<div className="dsv-detail__subtitle">Data Element Overview</div>
					</div>
				</Box>
				<Grid
					autoFlow="column"
					autoColumns="max-content"
					columnGap={1}
					align="center"
				>
					{canEditSchema && (
						<button
							className="dsv-btn dsv-btn--secondary"
							onClick={() => setLinkModalVisible(true)}
							data-testid="link-ucp-btn"
						>
							Manage Use Cases
						</button>
					)}
					{versionHistory.length >= 2 && (
						<button
							className="dsv-btn dsv-btn--secondary"
							onClick={handleCompareVersions}
							data-testid="compare-versions-btn"
						>
							Compare versions
						</button>
					)}
					{canEditSchema && (
						<button
							className="dsv-btn dsv-btn--primary"
							onClick={handleUpdateDataSchema}
						>
							+ Update Data Schema
						</button>
					)}
				</Grid>
			</Box>

			{/* Description — root `description` keyword from the schema. */}
			{schemaDescription && (
				<div className="dsv-detail__description">
					{schemaDescription}
				</div>
			)}

			{/* Summary Cards */}
			{currentVersionDetail && (
				<SummaryCards
					currentVersion={currentVersionDetail.version}
					status={currentVersionDetail.status}
					totalFields={fieldCount}
					affectedOrganisations={impactAnalysis?.subscribedOrganizations || 0}
				/>
			)}

			{/* Toggles row: affected organisations beside linked use cases. Both
			    toggles are page-owned and drive one shared full-width panel below
			    (accordion — opening one closes the other). The affected-orgs list
			    is gated by the same canEditSchema check as the schema-management
			    actions; since the /dsv routes are platform-admin-guarded this is
			    defense in depth rather than a user-facing split. */}
			<Grid columns={1} rowGap={0}>
				<Grid className="dsv-detail__toggles" autoFlow="column" autoColumns="max-content" columnGap={1.5}>
					{currentVersionDetail && canEditSchema && (
						<button
							type="button"
							className="dsv-detail__toggle"
							data-testid="affected-orgs-toggle"
							aria-expanded={expandedPanel === 'orgs'}
							aria-controls="dsv-detail-toggle-panel"
							onClick={() => setExpandedPanel((current) => (current === 'orgs' ? null : 'orgs'))}
						>
							{expandedPanel === 'orgs'
								? 'Hide affected organisations'
								: `View affected organisations (${orgs.length})`}
						</button>
					)}
					<button
						type="button"
						className="dsv-detail__toggle"
						data-testid="de-links-toggle"
						aria-expanded={expandedPanel === 'links'}
						aria-controls="dsv-detail-toggle-panel"
						onClick={() => setExpandedPanel((current) => (current === 'links' ? null : 'links'))}
					>
						{expandedPanel === 'links'
							? 'Hide linked use cases'
							: `View linked use cases (${linkedUseCaseCount})`}
					</button>
				</Grid>

				{expandedPanel && (
					<div id="dsv-detail-toggle-panel" className="dsv-detail__toggle-panel">
						{expandedPanel === 'orgs' && (
							<AffectedOrganisations organizations={impactAnalysis?.organizations} />
						)}
						{expandedPanel === 'links' && (
							<DeLinksSection
								links={deLinks}
								canEditSchema={canEditSchema}
								onRemove={setUnlinkTarget}
								panel
							/>
						)}
					</div>
				)}

				{/* Version History */}
				<Grid className="dsv-version-history" columns={1} rowGap={0}>
					<Box mb={5}>
						<h2 className="dsv-version-history__title">Version History</h2>
						<p className="dsv-version-history__subtitle">Complete audit trail of all schema versions</p>
					</Box>
					<Grid className="dsv-version-history__list" columns={1} rowGap={0}>
						{versionHistory.length > 0 ? (
							versionHistory.map((version) => (
								<VersionHistoryItem
									key={version.id}
									version={version}
									onViewSchema={handleViewSchema}
									canManage={canEditSchema}
									onDelete={setDeleteTarget}
									onEdit={(v) => { setEditServerError(''); setEditTarget(v); }}
								/>
							))
						) : (
							<div className="dsv-version-history__empty">
								No version history available.
							</div>
						)}
					</Grid>
				</Grid>
			</Grid>

			<LinkUseCasePersonaModal
				visible={linkModalVisible}
				hide={() => setLinkModalVisible(false)}
				dataElement={{
					dataElementId,
					elementName: currentVersionDetail?.elementName,
				}}
				deLinks={deLinks}
				onApplied={refreshDeLinks}
			/>

			<DeleteVersionModal
				visible={!!deleteTarget}
				hide={() => setDeleteTarget(null)}
				version={deleteTarget}
				onConfirm={handleConfirmDelete}
			/>

			<EditVersionNumberModal
				visible={!!editTarget}
				hide={() => setEditTarget(null)}
				version={editTarget}
				onConfirm={handleConfirmEdit}
				serverError={editServerError}
			/>

			<UnlinkUseCaseModal
				visible={!!unlinkTarget}
				hide={() => setUnlinkTarget(null)}
				target={unlinkTarget}
				onConfirm={handleConfirmUnlink}
			/>

			<Toast toastList={toastList} />

			{/* Loading overlay */}
			{loading &&
				ReactDOM.createPortal(
					<div className="modal white">
						<div className="loading">
							<img src={assetUrl("/img/loading.gif")} alt="loading" />
						</div>
					</div>,
					document.body
				)}
		</>
	);
};

export default DSVDetailPage;
